import React, { createContext, useContext, useEffect } from "react";
import useActiveTimeTracker from "../hooks/useActiveTimeTracker";
import { formatAttendanceDate } from "../utils/utils";

const ActiveTimeContext = createContext();

export function TrackerTimeProvider({ children, studentId, role }) {
  const tracker = useActiveTimeTracker(() => {}, studentId, role);
  const token = process.env.REACT_APP_API_TOKEN;
  const LMS = process.env.REACT_APP_API_LMS;

  async function markStudentAttendance() {
    const currentDate = new Date();
    const formattedDate = formatAttendanceDate(currentDate, true); // formatted like "YYYY-MM-DD"
    const lastAttendanceDate = localStorage.getItem("lastAttendanceDate");

    const existingData = localStorage.getItem(`activeTimeData:${studentId}:${role}`);
    if (existingData) {
      try {
        const parsed = JSON.parse(existingData);
        if (parsed.date === formattedDate) {
          tracker.start(parsed.time ?? 0);
          return;
        }
      } catch (err) {
        console.warn("Corrupt activeTimeData, re-fetching...");
      }
    }

    if (lastAttendanceDate !== formattedDate) {
      const raw = JSON.stringify({
        student_id: studentId,
        attendance_date: formatAttendanceDate(),
        status: "present",
        remarks: "Attended on time",
      });

      try {
        await fetch(`${LMS}/api/student-attendance`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: raw,
        });

        localStorage.setItem("lastAttendanceDate", formattedDate);
        const rawData = { date: formattedDate, time: 0 };
        localStorage.setItem(
          `activeTimeData:${studentId}:${role}`,
          JSON.stringify(rawData)
        );
        tracker.start(0);
      } catch (err) {
        console.error("Failed to mark attendance:", err);
      }
    } else {
      const raw = JSON.stringify({
        id: studentId,
        date: formattedDate,
      });

      try {
        const res = await fetch(`${LMS}/api/student/attendance`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: raw,
        });
        const data = await res.json();
        const rawData = {
          date: formattedDate,
          time:
            data?.data?.[0]?.time_spend === "Not available" ||
            data?.data?.[0]?.time_spend == null
              ? 0
              : Number(data.data[0].time_spend),
        };

        localStorage.setItem(
          `activeTimeData:${studentId}:${role}`,
          JSON.stringify(rawData)
        );
        tracker.start(rawData.time);
      } catch (err) {
        localStorage.removeItem(`activeTimeData:${studentId}:${role}`);
      }
    }
  }

  useEffect(() => {
    if (role === "Student") {
      markStudentAttendance();
    }
  }, []);

  return (
    <ActiveTimeContext.Provider value={tracker}>
      {children}
    </ActiveTimeContext.Provider>
  );
}

export function useActiveTime() {
  return useContext(ActiveTimeContext);
}
